import type { CSSProperties, FC, ReactNode } from 'react'

import { SectionLabel } from '@/components/generals/section-label'
import { SplitWords } from '@/components/generals/split-words'
import { VideoFacade } from '@/components/generals/video-facade'
import { PHOTO } from '@/constants/media'

const CHAPTERS = [
    'Bygger kalkylen for en 50 m² terrasse',
    'Forhåndsviser pristilbudet',
    'Tilpasser det til kunden',
    'Laster ned PDF-en, klar til sending'
]

const PIPS = [
    [28, 26], [72, 26],
    [28, 50], [72, 50],
    [28, 74], [72, 74]
]

/** A die showing six: the Norwegian "terningkast 6", the top review grade. */
const DieSix: FC = (): ReactNode => (
    <svg viewBox='0 0 100 100' className='die-six size-16 shrink-0 md:size-20' role='img' aria-label='Terningkast 6'>
        <rect x='4' y='4' width='92' height='92' rx='14' fill='#F4F6FB' />
        {PIPS.map(([cx, cy]) => (
            <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r='8.5' fill='#241D2D' />
        ))}
    </svg>
)

export const FieldVideo: FC = (): ReactNode => (
    <section id='se-det-i-aksjon' className='tone-night grain relative overflow-hidden py-24 md:py-36' data-header='dark'>
        <div className='wrap grid grid-cols-12 gap-x-6 gap-y-14'>
            <div className='col-span-12 lg:col-span-7' data-reveal='frame'>
                <VideoFacade
                    vimeoId='1218888137'
                    hash='c54c6c5a76'
                    title='Se en tømrer prise en 50 m² terrasse på under halvannet minutt'
                    poster={PHOTO.fenceDrilling}
                    meta='1 min 28 sek · filmet av kunden selv, med én hånd'
                />
            </div>

            <div className='col-span-12 flex flex-col lg:col-span-4 lg:col-start-9'>
                <SectionLabel index='07'>Ekte kunde, ekte kalkyle</SectionLabel>
                <h2 className='t-h2 mt-6 balance' data-reveal='words'>
                    <SplitWords text='Se en tømrer prise en 50 m² terrasse på under halvannet minutt.' />
                </h2>
                <p className='mt-6 text-tone-muted pretty'>
                    Ingen manus, ingen klipping underveis. Han filmer med den ene hånden og bruker Svenn med den andre, fra tom kalkyle til lønnsomt pristilbud klart til utsendelse, før kaffen rekker å bli lunken.
                </p>

                <ol className='mt-8 border-t border-tone-line'>
                    {CHAPTERS.map((chapter, i) => (
                        <li key={chapter} className='flex gap-4 border-b border-tone-line py-3 text-sm' data-reveal='rise' style={{ '--d': i } as CSSProperties}>
                            <span className='t-label tabular pt-0.5 text-signal'>{String(i + 1).padStart(2, '0')}</span>
                            {chapter}
                        </li>
                    ))}
                </ol>

                <figure className='mt-10 flex items-center gap-5'>
                    <DieSix />
                    <div>
                        <blockquote className='text-lg font-semibold leading-snug'>«Svenn Kalkulasjon får terningkast 6 av meg. Latterlig enkelt å bruke.»</blockquote>
                        <figcaption className='mt-2 text-sm text-tone-muted'>Kenneth, Tømrer Juvet AS</figcaption>
                    </div>
                </figure>
            </div>
        </div>
    </section>
)
